import { Icon } from '@assembly-js/design-system';
import clsx from 'clsx';
import React from 'react';

import { DiamondIcon } from 'src/components/icons';
import { SidebarThemeContext } from 'src/context';
import { SettingsPlanTag } from 'src/features/settings/SettingsPlanTag';
import history from 'src/history';

import {
  SettingsSidebarPlanBadge,
  SettingsSidebarRow as SettingsSidebarRowData,
} from './settingsSidebarSections';

const PlanBadge = ({ badge }: { badge: SettingsSidebarPlanBadge }) => {
  const { sidebarTextColor } = React.useContext(SidebarThemeContext);
  return badge.kind === 'planTag' ? (
    <SettingsPlanTag label={badge.label} />
  ) : (
    <DiamondIcon
      data-testid="upgrade-icon"
      style={{ fontSize: 16, color: sidebarTextColor }}
    />
  );
};

export const SettingsSidebarRow = ({
  row,
  onSelect,
}: {
  row: SettingsSidebarRowData;
  onSelect: (path: string) => void;
}) => {
  // Label and icon share the sidebar theme's text colour, like the main sidebar's rows.
  const { sidebarTextColor } = React.useContext(SidebarThemeContext);

  return (
    // A real link, so a row can be opened in a new tab or have its URL copied.
    <a
      href={history.createHref({ pathname: row.path })}
      data-testid={`settings-page-${row.id}`}
      aria-current={row.selected ? 'page' : undefined}
      onClick={(event) => {
        // Modified clicks keep the browser's new-tab / new-window behaviour.
        if (
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        event.preventDefault();
        onSelect(row.path);
      }}
      style={{ color: sidebarTextColor }}
      className={clsx(
        'box-border flex w-full cursor-pointer items-center gap-[9px] rounded px-2 py-1.5 text-left text-[13.5px] leading-5.5 no-underline',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#003DA5] focus-visible:outline-solid',
        row.selected ? 'bg-[#E9EBEE]' : 'bg-transparent hover:bg-gray-150',
      )}
    >
      <Icon icon={row.iconName} className="h-[15px] w-[15px] shrink-0" />
      <span className="min-w-0 flex-1 truncate">{row.label}</span>
      {row.planBadge && <PlanBadge badge={row.planBadge} />}
    </a>
  );
};
